export const M3_UNIT_11 = {
  id: "m3-u11-abilities",
  title: "Unit 11: Abilities Vocabulary",
  topic: "Master action verbs and ability expressions with present simple",
  iconType: "star",
  grammarImage: "/m3-u11-abilities.jpg",
  grammarText: "Learn action verbs with natural complements. Practice 'be good at/bad at' and present simple structures. A2-B1 level with integrated mini-readings.",
  sets: [
    // ===================================================================
    // SET 1: Communication Verbs (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set1",
      title: "Communication Verbs",
      questions: [
        {
          type: "mc",
          q: "Traduce al inglés: susurrar un secreto",
          options: ["shout a secret", "whisper a secret", "talk a secret", "speak a secret"],
          a: "whisper a secret",
          successtip: "✓ whisper = susurrar (voz baja) 🤫"
        },
        {
          type: "mc",
          q: "Traduce al español: shout instructions",
          options: ["susurrar instrucciones", "hablar instrucciones", "gritar instrucciones", "escuchar instrucciones"],
          a: "gritar instrucciones",
          successtip: "✓ shout = gritar (voz alta) 📢"
        },
        {
          type: "match",
          q: "Relaciona los verbos de comunicación:",
          pairs: [
            { left: "whisper", right: "susurrar" },
            { left: "shout", right: "gritar" },
            { left: "talk", right: "hablar (informal)" }
          ],
          a: "completed",
          successtip: "Niveles de voz: whisper < talk < shout 🔊"
        },
        {
          type: "mc",
          q: "She ___ three languages fluently.",
          options: ["speaks", "talks", "shouts", "whispers"],
          a: "speaks",
          successtip: "✓ speak languages = hablar idiomas. Use 'speak' with languages! 🌍"
        },
        {
          type: "mc",
          q: "Traduce: hablar por teléfono con la familia",
          options: ["speak on the phone with family", "talk on the phone with family", "shout on the phone with family"],
          a: "talk on the phone with family",
          successtip: "✓ talk = conversar (informal). 'Talk on the phone' es común! ☎️"
        },
        {
          type: "input",
          q: "Complete: Some people can ___ very quietly. (susurrar)",
          a: "whisper",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ can whisper = pueden susurrar (habilidad) 🤫"
        },
        {
          type: "mc",
          q: "¿Cuál es correcto? 'Hablar tres idiomas con fluidez'",
          options: ["talk three languages fluently", "speak three languages fluently", "shout three languages fluently"],
          a: "speak three languages fluently",
          successtip: "✓ SPEAK languages (no 'talk'). Important difference! 🗣️"
        },
        {
          type: "match",
          q: "Relaciona las frases completas:",
          pairs: [
            { left: "whisper a secret to a friend", right: "susurrar un secreto a un amigo" },
            { left: "shout instructions across the field", right: "gritar instrucciones al otro lado del campo" },
            { left: "speak English fluently", right: "hablar inglés con fluidez" }
          ],
          a: "completed",
          successtip: "Verbos + complementos naturales! 💪"
        },
        {
          type: "mc",
          q: "Coaches can ___ instructions during games.",
          options: ["whisper", "shout", "talk", "listen"],
          a: "shout",
          successtip: "✓ Coaches SHOUT (gritan) en el campo! 🏈"
        },
        {
          type: "input",
          q: "I ___ on the phone with my family every Sunday. (hablar)",
          a: "talk",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ talk on the phone = hablar por teléfono (informal) 📞"
        }
      ]
    },

    // ===================================================================
    // SET 2: Performance Actions (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set2",
      title: "Performance Actions",
      questions: [
        {
          type: "mc",
          q: "Traduce: actuar en una obra de teatro",
          options: ["play in a theater", "act in a play", "work in a play", "speak in a theater"],
          a: "act in a play",
          successtip: "✓ act in a play = actuar en una obra. 'Play' here = obra! 🎭"
        },
        {
          type: "mc",
          q: "Traduce: tocar el piano en un concierto",
          options: ["act the piano at a concert", "play the piano at a concert", "do the piano at a concert"],
          a: "play the piano at a concert",
          successtip: "✓ play the piano = tocar el piano (instrumento) 🎹"
        },
        {
          type: "match",
          q: "3 significados de 'play':",
          pairs: [
            { left: "play soccer", right: "jugar fútbol (deporte)" },
            { left: "play the guitar", right: "tocar la guitarra (música)" },
            { left: "see a play", right: "ver una obra (teatro)" }
          ],
          a: "completed",
          successtip: "✓ 'Play' tiene 3 significados diferentes! 🎯"
        },
        {
          type: "mc",
          q: "Professional actors can ___ in plays.",
          options: ["act", "play", "work", "speak"],
          a: "act",
          successtip: "✓ ACT in plays = actuar en obras. Actors ACT! 🎬"
        },
        {
          type: "input",
          q: "She can ___ the violin beautifully. (tocar)",
          a: "play",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ play + instrument = tocar instrumento musical 🎻"
        },
        {
          type: "mc",
          q: "Traduce: ver una obra en el teatro",
          options: ["act a play at the theater", "play a play at the theater", "see a play at the theater"],
          a: "see a play at the theater",
          successtip: "✓ SEE a play = ver una obra (espectador) 👀"
        },
        {
          type: "match",
          q: "Relaciona las frases completas:",
          pairs: [
            { left: "act in a Shakespeare play", right: "actuar en una obra de Shakespeare" },
            { left: "play the piano at a concert", right: "tocar el piano en un concierto" },
            { left: "see a play on Broadway", right: "ver una obra en Broadway" }
          ],
          a: "completed",
          successtip: "Act (actuar) vs Play (tocar/jugar) vs See (ver) 🎭🎹👁️"
        },
        {
          type: "mc",
          q: "I can ___ the guitar, but I can't ___ in plays.",
          options: ["play / act", "act / play", "play / play", "act / act"],
          a: "play / act",
          successtip: "✓ PLAY guitar (tocar) / ACT in plays (actuar) 🎸🎭"
        },
        {
          type: "input",
          q: "Musicians can ___ instruments at concerts. (tocar)",
          a: "play",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ Musicians PLAY instruments! 🎺"
        },
        {
          type: "mc",
          q: "Patrón correcto: 'tocar' un instrumento",
          options: ["play + the + instrument", "act + the + instrument", "do + the + instrument"],
          a: "play + the + instrument",
          successtip: "✓ PLAY THE piano/guitar/violin. Always 'THE' with instruments! 🎼"
        }
      ]
    },

    // ===================================================================
    // SET 3: Physical Movement (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set3",
      title: "Physical Movement",
      questions: [
        {
          type: "mc",
          q: "Traduce: saltar sobre una cerca",
          options: ["walk over a fence", "climb a fence", "jump over a fence", "run over a fence"],
          a: "jump over a fence",
          successtip: "✓ jump over = saltar sobre/por encima 🦘"
        },
        {
          type: "mc",
          q: "Traduce: escalar una montaña",
          options: ["jump a mountain", "walk a mountain", "climb a mountain", "drive a mountain"],
          a: "climb a mountain",
          successtip: "✓ climb = escalar/trepar 🧗"
        },
        {
          type: "match",
          q: "Formas de transporte:",
          pairs: [
            { left: "ride a bicycle", right: "montar en bicicleta" },
            { left: "drive a car", right: "conducir un carro" },
            { left: "walk to work", right: "caminar al trabajo" }
          ],
          a: "completed",
          successtip: "3 formas de transporte: ride (bici) / drive (carro) / walk (a pie) 🚴🚗🚶"
        },
        {
          type: "mc",
          q: "I ___ my bicycle to work every day.",
          options: ["drive", "ride", "walk", "climb"],
          a: "ride",
          successtip: "✓ RIDE a bicycle (montar bicicleta). Use 'ride' with bikes! 🚲"
        },
        {
          type: "input",
          q: "She can ___ a car in the city. (conducir)",
          a: "drive",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ drive a car = conducir un carro 🚗"
        },
        {
          type: "mc",
          q: "Athletes can ___ very high.",
          options: ["jump", "drive", "ride", "speak"],
          a: "jump",
          successtip: "✓ Athletes can JUMP high = atletas pueden saltar alto! 🏃‍♂️"
        },
        {
          type: "match",
          q: "Relaciona los verbos de movimiento:",
          pairs: [
            { left: "jump over a fence", right: "saltar sobre una cerca" },
            { left: "climb a mountain", right: "escalar una montaña" },
            { left: "walk five kilometers", right: "caminar cinco kilómetros" }
          ],
          a: "completed",
          successtip: "Verbos de movimiento vertical y horizontal! 📐"
        },
        {
          type: "mc",
          q: "Experienced hikers can ___ mountains.",
          options: ["drive", "ride", "climb", "jump"],
          a: "climb",
          successtip: "✓ Hikers CLIMB mountains = excursionistas escalan montañas! ⛰️"
        },
        {
          type: "input",
          q: "I ___ five kilometers every morning. (caminar)",
          a: "walk",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ walk = caminar (a pie) 🚶‍♀️"
        },
        {
          type: "mc",
          q: "¿Cuál NO es correcto? 'Conducir una bicicleta'",
          options: ["drive a bicycle", "ride a bicycle"],
          a: "drive a bicycle",
          successtip: "✗ DRIVE = solo vehículos motorizados. ✓ RIDE = bicicletas! 🚲"
        }
      ]
    },

    // ===================================================================
    // SET 4: Sports Actions (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set4",
      title: "Sports Actions",
      questions: [
        {
          type: "mc",
          q: "Traduce: patear una pelota en fútbol",
          options: ["throw a ball in soccer", "hit a ball in soccer", "kick a ball in soccer", "catch a ball in soccer"],
          a: "kick a ball in soccer",
          successtip: "✓ kick = patear (con el pie) ⚽"
        },
        {
          type: "mc",
          q: "Traduce: lanzar una pelota lejos",
          options: ["kick a ball far", "hit a ball far", "throw a ball far", "catch a ball far"],
          a: "throw a ball far",
          successtip: "✓ throw = lanzar (con la mano) 🏐"
        },
        {
          type: "match",
          q: "Acciones deportivas básicas:",
          pairs: [
            { left: "kick", right: "patear" },
            { left: "throw", right: "lanzar" },
            { left: "catch", right: "atrapar" }
          ],
          a: "completed",
          successtip: "3 acciones esenciales en deportes! 🏀"
        },
        {
          type: "mc",
          q: "Soccer players can ___ balls accurately.",
          options: ["throw", "kick", "catch", "hit"],
          a: "kick",
          successtip: "✓ Soccer = KICK (patear con el pie) ⚽"
        },
        {
          type: "input",
          q: "He can ___ a tennis ball hard. (golpear)",
          a: "hit",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ hit = golpear (tenis, béisbol) 🎾"
        },
        {
          type: "mc",
          q: "Traduce: atrapar una pelota en el aire",
          options: ["throw a flying ball", "kick a flying ball", "catch a flying ball", "hit a flying ball"],
          a: "catch a flying ball",
          successtip: "✓ catch = atrapar. 'Flying' = en el aire! 🤾"
        },
        {
          type: "match",
          q: "Relaciona las frases completas:",
          pairs: [
            { left: "kick a ball in soccer", right: "patear una pelota en fútbol" },
            { left: "throw a baseball far", right: "lanzar una pelota de béisbol lejos" },
            { left: "catch a flying ball", right: "atrapar una pelota en el aire" }
          ],
          a: "completed",
          successtip: "Verbos deportivos + complementos naturales! ⚾"
        },
        {
          type: "mc",
          q: "Baseball players can ___ balls very far.",
          options: ["kick", "throw", "catch", "speak"],
          a: "throw",
          successtip: "✓ Baseball = THROW (lanzar con la mano) ⚾"
        },
        {
          type: "input",
          q: "Tennis players ___ the ball hard. (golpear)",
          a: "hit",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ HIT the ball = golpear la pelota (tenis, béisbol) 🎾"
        },
        {
          type: "mc",
          q: "Ordena por intensidad: patear, lanzar, golpear",
          options: ["kick, throw, hit", "throw, hit, kick", "hit, kick, throw"],
          a: "kick, throw, hit",
          successtip: "Fuerza: kick (pie) < throw (mano) < hit (raqueta/bate) 💪"
        }
      ]
    },

    // ===================================================================
    // SET 5: Be Good At / Be Bad At (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set5",
      title: "Be Good At / Be Bad At",
      questions: [
        {
          type: "mc",
          q: "Traduce: Soy bueno en recordar nombres",
          options: ["I'm good at remember names", "I'm good at remembering names", "I'm good to remember names"],
          a: "I'm good at remembering names",
          successtip: "✓ be good at + verbo-ING (gerundio). Always! 📝"
        },
        {
          type: "mc",
          q: "Traduce: Ella es mala en deletrear palabras",
          options: ["She's bad at spell words", "She's bad at spelling words", "She's bad to spell words"],
          a: "She's bad at spelling words",
          successtip: "✓ be bad at + verbo-ING. Same pattern as 'good at'! ✍️"
        },
        {
          type: "match",
          q: "Estructura 'be good/bad at':",
          pairs: [
            { left: "I'm good at", right: "Soy bueno en" },
            { left: "She's bad at", right: "Ella es mala en" },
            { left: "They're good at", right: "Ellos son buenos en" }
          ],
          a: "completed",
          successtip: "Patrón: I'm/He's/She's/They're + good/bad at + ING 🎯"
        },
        {
          type: "mc",
          q: "I'm good at ___ phone numbers.",
          options: ["remember", "remembering", "to remember", "remembered"],
          a: "remembering",
          successtip: "✓ good at + REMEMBERING (verbo-ING). Never infinitive! 🧠"
        },
        {
          type: "input",
          q: "He's bad at ___ difficult words. (deletrear)",
          a: "spelling",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ bad at + SPELLING (ING). Pattern: bad at + verbo-ING! 📚"
        },
        {
          type: "mc",
          q: "She ___ good at understanding English.",
          options: ["am", "is", "are", "be"],
          a: "is",
          successtip: "✓ SHE IS good at. Verb to be: I am, you are, she IS! 👍"
        },
        {
          type: "match",
          q: "Relaciona las habilidades cognitivas:",
          pairs: [
            { left: "be good at remembering names", right: "ser bueno recordando nombres" },
            { left: "be bad at spelling words", right: "ser malo deletreando palabras" },
            { left: "be good at understanding instructions", right: "ser bueno entendiendo instrucciones" }
          ],
          a: "completed",
          successtip: "Habilidades cognitivas con 'be good/bad at' + ING 🧠"
        },
        {
          type: "mc",
          q: "I'm not good at ___ in my head. (restar)",
          options: ["subtract", "subtracting", "to subtract"],
          a: "subtracting",
          successtip: "✓ I'm NOT good at + SUBTRACTING. Negative = NOT good at! ➖"
        },
        {
          type: "input",
          q: "They're good at ___ difficult problems. (pensar en)",
          a: "thinking about",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ thinking about = pensar en. Phrasal expression! 🤔"
        },
        {
          type: "mc",
          q: "Negativo de 'I'm good at swimming':",
          options: ["I'm bad at swimming", "I'm not good at swimming", "Ambas son correctas"],
          a: "Ambas son correctas",
          successtip: "✓ I'm BAD at = I'm NOT good at. Both work! 🏊"
        }
      ]
    },

    // ===================================================================
    // SET 6: Strength & Carrying (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set6",
      title: "Strength & Carrying",
      questions: [
        {
          type: "mc",
          q: "Traduce: cargar cajas pesadas",
          options: ["lift heavy boxes", "move heavy boxes", "carry heavy boxes", "throw heavy boxes"],
          a: "carry heavy boxes",
          successtip: "✓ carry = cargar (en las manos) 📦"
        },
        {
          type: "mc",
          q: "Traduce: levantar pesas en el gimnasio",
          options: ["carry weights at the gym", "move weights at the gym", "lift weights at the gym"],
          a: "lift weights at the gym",
          successtip: "✓ lift weights = levantar pesas 🏋️"
        },
        {
          type: "match",
          q: "Verbos de fuerza física:",
          pairs: [
            { left: "carry", right: "cargar (en manos)" },
            { left: "lift", right: "levantar (del suelo)" },
            { left: "move", right: "mover (de lugar)" }
          ],
          a: "completed",
          successtip: "3 verbos diferentes de fuerza! 💪"
        },
        {
          type: "mc",
          q: "Strong workers can ___ heavy boxes.",
          options: ["carry", "speak", "remember", "spell"],
          a: "carry",
          successtip: "✓ Workers CARRY boxes = trabajadores cargan cajas! 📦"
        },
        {
          type: "input",
          q: "She can ___ furniture to another room. (mover)",
          a: "move",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ move furniture = mover muebles 🛋️"
        },
        {
          type: "mc",
          q: "Traduce: mover muebles a otra habitación",
          options: ["carry furniture to another room", "lift furniture to another room", "move furniture to another room"],
          a: "move furniture to another room",
          successtip: "✓ MOVE to = mover hacia/a otro lugar 🚚"
        },
        {
          type: "match",
          q: "Relaciona las frases completas:",
          pairs: [
            { left: "carry heavy bags", right: "cargar bolsas pesadas" },
            { left: "lift weights", right: "levantar pesas" },
            { left: "move furniture", right: "mover muebles" }
          ],
          a: "completed",
          successtip: "Carry (cargar) ≠ Lift (levantar) ≠ Move (mover) 🏋️"
        },
        {
          type: "mc",
          q: "I can ___ this box. It's not heavy.",
          options: ["carry", "speak", "remember", "understand"],
          a: "carry",
          successtip: "✓ CARRY this box = cargar esta caja (peso manejable) 📦"
        },
        {
          type: "input",
          q: "Strong people can ___ heavy weights. (levantar)",
          a: "lift",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ LIFT = levantar (desde el suelo) 🏋️‍♀️"
        },
        {
          type: "mc",
          q: "Diferencia: carry vs lift",
          options: ["carry = en manos / lift = del suelo", "carry = del suelo / lift = en manos", "son iguales"],
          a: "carry = en manos / lift = del suelo",
          successtip: "✓ CARRY (llevar en manos) ≠ LIFT (levantar del suelo) 💡"
        }
      ]
    },

    // ===================================================================
    // SET 7: Present Simple with Do/Does (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set7",
      title: "Present Simple: Do/Does",
      questions: [
        {
          type: "mc",
          q: "I ___ homework every evening.",
          options: ["do", "does", "doing", "did"],
          a: "do",
          successtip: "✓ I/you/we/they DO. Present simple! 📚"
        },
        {
          type: "mc",
          q: "She ___ from home three days a week.",
          options: ["work", "works", "working", "worked"],
          a: "works",
          successtip: "✓ He/she/it WORKS (+ S). Third person adds S! 💼"
        },
        {
          type: "match",
          q: "Presente simple - afirmativo:",
          pairs: [
            { left: "I work", right: "Yo trabajo" },
            { left: "She works", right: "Ella trabaja" },
            { left: "They work", right: "Ellos trabajan" }
          ],
          a: "completed",
          successtip: "I/you/we/they work. He/she/it workS! ⚙️"
        },
        {
          type: "mc",
          q: "___ you walk to work every day?",
          options: ["Do", "Does", "Are", "Is"],
          a: "Do",
          successtip: "✓ DO you...? Questions: DO I/you/we/they. DOES he/she/it! ❓"
        },
        {
          type: "input",
          q: "___ she work from home? (pregunta)",
          a: "Does",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ DOES she work? Third person = DOES! 🏠"
        },
        {
          type: "mc",
          q: "She ___ work on weekends.",
          options: ["don't", "doesn't", "not", "no"],
          a: "doesn't",
          successtip: "✓ She DOESN'T work. Negative: I/you DON'T, he/she DOESN'T! ❌"
        },
        {
          type: "match",
          q: "Presente simple - negativo:",
          pairs: [
            { left: "I don't work", right: "No trabajo" },
            { left: "She doesn't work", right: "Ella no trabaja" },
            { left: "They don't work", right: "Ellos no trabajan" }
          ],
          a: "completed",
          successtip: "DON'T (I/you/we/they) vs DOESN'T (he/she/it) 🚫"
        },
        {
          type: "mc",
          q: "I ___ sit at a desk all day.",
          options: ["don't", "doesn't", "not", "no"],
          a: "don't",
          successtip: "✓ I DON'T sit. Negative with I/you/we/they! 🪑"
        },
        {
          type: "input",
          q: "He ___ homework every night. (hacer - afirmativo)",
          a: "does",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ He DOES homework. Third person = DOES! 📝"
        },
        {
          type: "mc",
          q: "Estructura pregunta: '¿Trabajas desde casa?'",
          options: ["You work from home?", "Do you work from home?", "Does you work from home?"],
          a: "Do you work from home?",
          successtip: "✓ DO + subject + verb. Questions need DO/DOES! 🏡"
        }
      ]
    },

    // ===================================================================
    // SET 8: Sensory Verbs (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set8",
      title: "Sensory Verbs: See, Listen, Hear",
      questions: [
        {
          type: "mc",
          q: "Traduce: ver claramente sin lentes",
          options: ["hear clearly without glasses", "listen clearly without glasses", "see clearly without glasses"],
          a: "see clearly without glasses",
          successtip: "✓ see = ver (percepción visual) 👓"
        },
        {
          type: "mc",
          q: "Traduce: escuchar música cuidadosamente",
          options: ["see music carefully", "listen to music carefully", "hear music carefully"],
          a: "listen to music carefully",
          successtip: "✓ LISTEN TO = escuchar (atención activa) 🎵"
        },
        {
          type: "match",
          q: "Verbos sensoriales:",
          pairs: [
            { left: "see", right: "ver" },
            { left: "listen", right: "escuchar (activo)" },
            { left: "hear", right: "oír (pasivo)" }
          ],
          a: "completed",
          successtip: "See (ver) / Listen (escuchar activo) / Hear (oír pasivo) 👂"
        },
        {
          type: "mc",
          q: "I can ___ sounds from far away.",
          options: ["see", "listen", "hear", "watch"],
          a: "hear",
          successtip: "✓ HEAR = oír (percepción pasiva, sin esfuerzo) 🔊"
        },
        {
          type: "input",
          q: "Musicians ___ to music very carefully. (escuchar)",
          a: "listen",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ LISTEN (escuchar con atención). Don't forget 'TO'! 🎧"
        },
        {
          type: "mc",
          q: "People with good vision can ___ clearly.",
          options: ["hear", "listen", "see", "speak"],
          a: "see",
          successtip: "✓ SEE clearly = ver claramente (visión) 👁️"
        },
        {
          type: "match",
          q: "Relaciona las frases completas:",
          pairs: [
            { left: "see clearly without glasses", right: "ver claramente sin lentes" },
            { left: "listen to music carefully", right: "escuchar música cuidadosamente" },
            { left: "hear sounds from far away", right: "oír sonidos desde lejos" }
          ],
          a: "completed",
          successtip: "3 verbos sensoriales con complementos naturales! 👂👁️"
        },
        {
          type: "mc",
          q: "Diferencia: listen vs hear",
          options: ["listen = activo / hear = pasivo", "listen = pasivo / hear = activo", "son iguales"],
          a: "listen = activo / hear = pasivo",
          successtip: "✓ LISTEN (escuchar activamente) ≠ HEAR (oír sin esfuerzo) 🎧"
        },
        {
          type: "input",
          q: "I can't ___ clearly without my glasses. (ver)",
          a: "see",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ I can't SEE = No puedo ver (habilidad visual) 👓"
        },
        {
          type: "mc",
          q: "'Escuchar música' - ¿Cuál es correcto?",
          options: ["listen music", "listen to music", "hear to music"],
          a: "listen to music",
          successtip: "✓ LISTEN TO (always needs TO!) 🎶"
        }
      ]
    },

    // ===================================================================
    // SET 9: Academic & Math Skills (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set9",
      title: "Academic & Math Skills",
      questions: [
        {
          type: "mc",
          q: "She ___ large numbers quickly.",
          options: ["add", "adds", "adding", "added"],
          a: "adds",
          successtip: "✓ She ADDS (third person + S). Present simple! ➕"
        },
        {
          type: "mc",
          q: "Traduce: restar números en mi cabeza",
          options: ["add numbers in my head", "subtract numbers in my head", "multiply numbers in my head"],
          a: "subtract numbers in my head",
          successtip: "✓ subtract = restar. In my head = mentalmente! ➖"
        },
        {
          type: "match",
          q: "Operaciones matemáticas:",
          pairs: [
            { left: "add", right: "sumar" },
            { left: "subtract", right: "restar" },
            { left: "multiply", right: "multiplicar" }
          ],
          a: "completed",
          successtip: "Math operations: add (+), subtract (-), multiply (×) 🔢"
        },
        {
          type: "mc",
          q: "I ___ understand complex instructions.",
          options: ["don't", "doesn't", "not", "no"],
          a: "don't",
          successtip: "✓ I DON'T understand. Negative with I/you/we/they! 🤔"
        },
        {
          type: "input",
          q: "Math teachers can ___ large numbers quickly. (sumar)",
          a: "add",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ ADD = sumar (operación matemática) ➕"
        },
        {
          type: "mc",
          q: "___ you subtract numbers in your head?",
          options: ["Can", "Do", "Ambas correctas", "Ninguna"],
          a: "Ambas correctas",
          successtip: "✓ CAN (habilidad) vs DO (hábito). Both work! 🧮"
        },
        {
          type: "match",
          q: "Habilidades académicas:",
          pairs: [
            { left: "spell words", right: "deletrear palabras" },
            { left: "understand instructions", right: "entender instrucciones" },
            { left: "remember names", right: "recordar nombres" }
          ],
          a: "completed",
          successtip: "Academic abilities: spell, understand, remember 📚"
        },
        {
          type: "mc",
          q: "Some students can ___ in their heads.",
          options: ["subtract numbers", "subtract", "subtracting numbers"],
          a: "subtract numbers",
          successtip: "✓ After CAN use base verb + object! 🧠"
        },
        {
          type: "input",
          q: "She's bad at ___ English words. (deletrear)",
          a: "spelling",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ bad at + SPELLING (ING). Remember the pattern! ✍️"
        },
        {
          type: "mc",
          q: "Good students can ___ complex instructions.",
          options: ["understand", "understands", "understanding"],
          a: "understand",
          successtip: "✓ can + UNDERSTAND (base form). No S after CAN! 📖"
        }
      ]
    },

    // ===================================================================
    // SET 10: Integration & Can (10 preguntas)
    // ===================================================================
    {
      id: "m3-u11-set10",
      title: "Integration: Abilities with Can",
      questions: [
        {
          type: "mc",
          q: "Traduce: Puedo actuar en obras pero soy malo tocando instrumentos",
          options: [
            "I can act in plays but I'm bad at play instruments",
            "I can act in plays but I'm bad at playing instruments",
            "I act in plays but I'm bad at playing instruments"
          ],
          a: "I can act in plays but I'm bad at playing instruments",
          successtip: "✓ I CAN act (habilidad) + I'm bad AT playing (ING)! 🎭🎸"
        },
        {
          type: "mc",
          q: "She's good at climbing but she ___ swim.",
          options: ["can", "can't", "don't", "doesn't"],
          a: "can't",
          successtip: "✓ CAN'T = cannot (incapacidad). Contraste con 'good at'! 🧗‍♀️🏊"
        },
        {
          type: "match",
          q: "Estructuras de habilidad:",
          pairs: [
            { left: "I can swim", right: "Puedo nadar (capacidad)" },
            { left: "I'm good at swimming", right: "Soy bueno nadando (nivel)" },
            { left: "I swim every day", right: "Nado cada día (hábito)" }
          ],
          a: "completed",
          successtip: "3 formas de hablar de habilidades! CAN / be good at / present simple 🏊"
        },
        {
          type: "mc",
          q: "Some people ___ speak many languages.",
          options: ["can", "do", "are", "have"],
          a: "can",
          successtip: "✓ CAN speak = pueden hablar (habilidad natural) 🌍"
        },
        {
          type: "input",
          q: "Athletes ___ jump very high. (pueden)",
          a: "can",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ CAN = pueden (habilidad física) 🏃‍♂️"
        },
        {
          type: "order",
          q: "Ordena: She / at / good / is / climbing / mountains",
          words: ["She", "is", "good", "at", "climbing", "mountains."],
          a: "She is good at climbing mountains.",
          successtip: "✓ She IS good AT climbing mountains. Pattern: be + good at + ING! ⛰️"
        },
        {
          type: "mc",
          q: "I ___ speak English but I ___ speak French.",
          options: ["can / can", "can / can't", "can't / can"],
          a: "can / can't",
          successtip: "✓ CAN (puedo) vs CAN'T (no puedo). Contraste de habilidades! 🗣️"
        },
        {
          type: "match",
          q: "Relaciona habilidades completas:",
          pairs: [
            { left: "I'm good at remembering names", right: "Soy bueno recordando nombres" },
            { left: "She can't spell difficult words", right: "Ella no puede deletrear palabras difíciles" },
            { left: "They understand complex instructions", right: "Ellos entienden instrucciones complejas" }
          ],
          a: "completed",
          successtip: "3 estructuras integradas: be good at / can't / present simple! 🎯"
        },
        {
          type: "mc",
          q: "Musicians can ___ instruments, actors can ___ in plays.",
          options: ["play / act", "act / play", "speak / talk"],
          a: "play / act",
          successtip: "✓ Musicians PLAY instruments / Actors ACT in plays! 🎸🎭"
        },
        {
          type: "input",
          q: "Are you good ___ sports or academic activities? (preposición)",
          a: "at",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ good AT (always use AT after good/bad!) 🏀📚"
        }
      ]
    }
    ],

 readingSet: {
  id: "m3-u11-reading",
  title: "Reading: Talents and Skills",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Mark's Skills (preguntas 1-2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Mark's Profile**

Hi! I'm Mark. I can speak three languages: English, Spanish, and French. I'm also good at playing the guitar. Music and languages are my favorite things to learn!`
    },
    {
      type: "mc",
      q: "How many languages can Mark speak?",
      options: ["Two", "Three", "Four"],
      a: "Three",
      successtip: "✓ POLYGLOT!\nMark can speak English, Spanish, and French. 🌍"
    },
    {
      type: "mc",
      q: "What is Mark good at?",
      options: ["Playing the guitar", "Playing soccer", "Acting in plays"],
      a: "Playing the guitar",
      successtip: "✓ MUSICAL TALENT!\nMark is good at playing the guitar. 🎸"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Sarah the Actress (preguntas 3-4)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Sarah's Career**

My name is Sarah. I'm a professional actress. I can act in plays and movies. I'm also good at dancing, which helps me in musical theater.`
    },
    {
      type: "mc",
      q: "What is Sarah's profession?",
      options: ["Actress", "Teacher", "Musician"],
      a: "Actress",
      successtip: "✓ STAGE STAR!\nSarah acts in plays and movies professionally. 🎬"
    },
    {
      type: "mc",
      q: "What else is Sarah good at besides acting?",
      options: ["Singing", "Dancing", "Swimming"],
      a: "Dancing",
      successtip: "✓ TALENTED!\nDancing is a great skill for an actress. 💃"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Strength and Math (preguntas 5-8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Tom and Linda**

Tom works at a gym. He's very strong. He can lift heavy weights and carry big boxes. He's good at helping people exercise. 

My sister Linda is a math teacher. She can add and subtract large numbers very quickly. She's also good at explaining difficult problems.`
    },
    {
      type: "mc",
      q: "Where does Tom work?",
      options: ["At a school", "At a gym", "At a hospital"],
      a: "At a gym",
      successtip: "✓ FITNESS!\nTom's strength is perfect for his job at the gym. 🏋️"
    },
    {
      type: "mc",
      q: "What can Tom do thanks to his strength?",
      options: ["Lift heavy weights", "Play the piano", "Speak three languages"],
      a: "Lift heavy weights",
      successtip: "✓ POWER!\nHe can lift heavy weights and carry big boxes. 💪"
    },
    {
      type: "mc",
      q: "What is Linda's job?",
      options: ["Math teacher", "English teacher", "Gym teacher"],
      a: "Math teacher",
      successtip: "✓ NUMBERS!\nLinda teaches mathematics to her students. 🧮"
    },
    {
      type: "mc",
      q: "What can Linda do very quickly?",
      options: ["Speak languages", "Add and subtract numbers", "Play instruments"],
      a: "Add and subtract numbers",
      successtip: "✓ MATH GENIUS!\nShe is very fast with addition and subtraction. ➕➖"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Sports vs. Music (preguntas 9-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Personal Interests**

I'm not good at sports. I can't kick a ball or throw a baseball. But I'm very good at playing the piano and listening to music. I find music much more interesting than soccer!`
    },
    {
      type: "mc",
      q: "What is this person NOT good at?",
      options: ["Sports", "Music", "Languages"],
      a: "Sports",
      successtip: "✓ HONESTY!\nThe person can't kick or throw a ball well. 🏀❌"
    },
    {
      type: "mc",
      q: "What IS the person good at?",
      options: ["Playing the piano", "Playing soccer", "Acting in plays"],
      a: "Playing the piano",
      successtip: "✓ FINISHED!\nFantastic! You understand how to describe abilities. 🎹🏆"
    }
  ]
},

   Reviewset: {
      id: "m3-u11-review",
      title: "Unit 11 Review",
      questions: [
        {
          type: "match",
          q: "Final Review: Communication Verbs",
          pairs: [
            { left: "whisper", right: "susurrar" },
            { left: "shout", right: "gritar" },
            { left: "speak", right: "hablar (idiomas)" }
          ],
          a: "completed",
          successtip: "✓ Communication verbs mastered! whisper < talk < shout! 🗣️"
        },
        {
          type: "mc",
          q: "She ___ the piano beautifully.",
          options: ["plays", "acts", "does", "speaks"],
          a: "plays",
          successtip: "✓ PLAY the piano = tocar el piano! Third person + S! 🎹"
        },
        {
          type: "input",
          q: "I'm good at ___ names. (recordar)",
          a: "remembering",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ good at + REMEMBERING (ING). Always gerund after 'good at'! 🧠"
        },
        {
          type: "mc",
          q: "Athletes can ___ very high.",
          options: ["jump", "jumps", "jumping", "jumped"],
          a: "jump",
          successtip: "✓ can + JUMP (base form). No S after CAN! 🦘"
        },
        {
          type: "order",
          q: "Ordena: She / doesn't / from / work / home",
          words: ["She", "doesn't", "work", "from", "home."],
          a: "She doesn't work from home.",
          successtip: "✓ She DOESN'T work. Negative: doesn't + base verb! 🏠"
        },
        {
          type: "match",
          q: "Match sports actions:",
          pairs: [
            { left: "kick a ball", right: "patear una pelota" },
            { left: "throw a ball", right: "lanzar una pelota" },
            { left: "catch a ball", right: "atrapar una pelota" }
          ],
          a: "completed",
          successtip: "✓ Sports actions: kick / throw / catch! ⚽"
        },
        {
          type: "mc",
          q: "___ you speak English?",
          options: ["Do", "Does", "Are", "Can"],
          a: "Do",
          successtip: "✓ DO you speak? Questions: DO/DOES + subject + verb! ❓"
        },
        {
          type: "input",
          q: "Strong people can ___ heavy weights. (levantar)",
          a: "lift",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ LIFT = levantar (del suelo). CAN + base verb! 🏋️"
        },
        {
          type: "match",
          q: "Match ability structures:",
          pairs: [
            { left: "I can swim", right: "Puedo nadar" },
            { left: "I'm good at swimming", right: "Soy bueno nadando" },
            { left: "I swim every day", right: "Nado cada día" }
          ],
          a: "completed",
          successtip: "✓ 3 ways to express abilities! can / be good at / present simple! 🏊"
        },
        {
          type: "mc",
          q: "I ___ understand complex instructions.",
          options: ["can't", "doesn't", "am not", "not"],
          a: "can't",
          successtip: "✓ I CAN'T understand = No puedo entender! You completed Unit 11! 🎉🎊"
        }
      ]
    }
};
